import { useEffect, useRef, useCallback, useState } from 'react';
import type {
  ScoreSubmitPayload,
  ScoreUpdatedPayload,
  GameStatusChangedPayload,
  GameStateSyncPayload,
  ScoreSource,
} from '../types/index.js';

export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface UseGameSocketOptions {
  realtimeUrl: string;
  gameId: string;
  source: ScoreSource;
  onScoreUpdated?: (payload: ScoreUpdatedPayload) => void;
  onStatusChanged?: (payload: GameStatusChangedPayload) => void;
  onStateSync?: (payload: GameStateSyncPayload) => void;
}

export interface UseGameSocketReturn {
  status: SocketStatus;
  submitScore: (payload: Omit<ScoreSubmitPayload, 'gameId' | 'source'>) => void;
  /** True when the last score update came from a different source than ours */
  externalUpdate: boolean;
  clearExternalUpdate: () => void;
}

/**
 * Manages a Socket.io connection for a single game room.
 * Dynamically imports socket.io-client to keep it out of SSR paths.
 */
export function useGameSocket(options: UseGameSocketOptions): UseGameSocketReturn {
  const { realtimeUrl, gameId, source, onScoreUpdated, onStatusChanged, onStateSync } = options;

  // We use `any` here because socket.io-client types vary and this hook is
  // consumed across multiple apps — each app provides its own socket.io-client.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const socketRef = useRef<any>(null);
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [externalUpdate, setExternalUpdate] = useState(false);

  const submitScore = useCallback(
    (payload: Omit<ScoreSubmitPayload, 'gameId' | 'source'>) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('score:submit', { ...payload, gameId, source });
      }
    },
    [gameId, source],
  );

  const clearExternalUpdate = useCallback(() => setExternalUpdate(false), []);

  useEffect(() => {
    let cancelled = false;

    async function connect() {
      // Dynamic import — socket.io-client must be installed in the consuming app
      const { io } = await import('socket.io-client' as string);

      if (cancelled) return;

      const socket = io(realtimeUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        // When proxied through nginx at /socket.io/, the path is the default.
        // Direct connections to port 3201 also use the default path.
        path: '/socket.io/',
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        if (!cancelled) {
          setStatus('connected');
          socket.emit('game:join', { gameId, source });
        }
      });

      socket.on('disconnect', () => {
        if (!cancelled) setStatus('disconnected');
      });

      socket.on('connect_error', () => {
        if (!cancelled) setStatus('error');
      });

      socket.on('score:updated', (payload: ScoreUpdatedPayload) => {
        if (cancelled) return;
        // Flag if the update came from a different source
        if (payload.source !== source) setExternalUpdate(true);
        onScoreUpdated?.(payload);
      });

      socket.on('game:statusChanged', (payload: GameStatusChangedPayload) => {
        if (!cancelled) onStatusChanged?.(payload);
      });

      socket.on('game:stateSync', (payload: GameStateSyncPayload) => {
        if (!cancelled) onStateSync?.(payload);
      });
    }

    connect().catch(() => setStatus('error'));

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.emit('game:leave', { gameId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [realtimeUrl, gameId, source]); // intentionally omit callbacks — stable via ref if needed

  return { status, submitScore, externalUpdate, clearExternalUpdate };
}
